/**
 * Centralized API Client with Redux Integration
 * 
 * This API client pulls the authentication token directly from the Redux store
 * and automatically injects it into all HTTP requests via Axios interceptors.
 * 
 * Features:
 * - Automatic token injection from Redux store
 * - Type-safe generic methods (get, post, put, delete)
 * - Global error handling with 401 auto-logout
 * - ResourceNotFound handling
 * - Portal header injection
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config/appConfig';
import { store } from '../store/configureStore';
import { clearAuth } from '../store/auth/reducer';
import { selectToken, selectUserEmail } from '../store/auth/selectors';
import { MapiError, RequestError, UnauthorizedError } from '../errors';
import { logger } from '../services/logger';

/**
 * Generate a unique correlation ID for each request.
 * Uses native crypto.randomUUID() (available in all modern browsers).
 */
const generateCorrelationId = (): string => {
    return crypto.randomUUID();
};

const normalizePathSegment = (segment: string): string => {
    try {
        return encodeURIComponent(decodeURIComponent(segment));
    } catch {
        return encodeURIComponent(segment);
    }
};

const normalizeRequestUrl = (url?: string): string | undefined => {
    if (!url) {
        return url;
    }

    const hashIndex = url.indexOf('#');
    const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const hash = hashIndex >= 0 ? url.slice(hashIndex + 1) : '';

    const queryIndex = withoutHash.indexOf('?');
    const pathPart = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
    const queryPart = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '';

    let normalizedPath = pathPart;
    if (/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(pathPart)) {
        const parsed = new URL(pathPart);
        parsed.pathname = parsed.pathname
            .split('/')
            .map((segment) => (segment ? normalizePathSegment(segment) : segment))
            .join('/');
        normalizedPath = `${parsed.origin}${parsed.pathname}`;
    } else {
        normalizedPath = pathPart
            .split('/')
            .map((segment) => (segment ? normalizePathSegment(segment) : segment))
            .join('/');
    }

    const normalizedQuery = queryPart ? new URLSearchParams(queryPart).toString() : '';

    return `${normalizedPath}${normalizedQuery ? `?${normalizedQuery}` : ''}${hash ? `#${hash}` : ''}`;
};

/**
 * Create the base Axios instance with default configuration
 * Default baseURL uses sandbox; switched dynamically via setApiEnvironment()
 */
const apiClient: AxiosInstance = axios.create({
    baseURL: config.apiSandboxUrl.replace(/\/$/, ''), // Default to sandbox
    headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 second timeout
});

/**
 * Switch the API client base URL based on the selected environment.
 * Called when the user toggles between sandbox and production.
 */
export const setApiEnvironment = (environment: 'sandbox' | 'production') => {
    const url = environment === 'sandbox'
        ? config.apiSandboxUrl
        : config.apiProductionUrl;
    apiClient.defaults.baseURL = url.replace(/\/$/, '');
};

/**
 * Request Interceptor
 * 
 * Automatically injects:
 * 1. Bearer token from Redux store
 * 2. Portal header (x-portal-name)
 * 3. API version query parameter
 */
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        try {
            config.url = normalizeRequestUrl(config.url);

            const state = store.getState();
            const token = selectToken(state as any);
            const userEmail = selectUserEmail(state as any);
            
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            } else {
                logger.trackWarning('No token found in Redux store', 'apiClient.request');
            }
            
            // Add user email header if available
            if (userEmail) {
                config.headers['x-user-email'] = userEmail;
            } else {
                logger.trackWarning('No user email found in Redux store', 'apiClient.request');
            }
            
            if (!config.headers['x-portal-name']) {
                config.headers['x-portal-name'] = 'developer-portal-v2';
            }

            // Generate and attach a unique Correlation ID for every request
            const correlationId = generateCorrelationId();
            config.headers['X-Correlation-Id'] = correlationId;
            // Store on config for reliable extraction in error handler (Axios 1.x AxiosHeaders workaround)
            (config as any)._correlationId = correlationId;

            return config;
        } catch (error) {
            logger.trackError(error instanceof Error ? error : new Error(String(error)), 'apiClient.request');
            return Promise.reject(error);
        }
    },
    (error) => {
        logger.trackError(error instanceof Error ? error : new Error(String(error)), 'apiClient.request');
        return Promise.reject(error);
    }
);

/**
 * Response Interceptor
 * 
 * Handles global error responses:
 * - 401: Unauthorized (triggers logout via Redux)
 * - 404: Resource not found (maps to ResourceNotFound)
 * - 429: Too many requests
 * - 500/502: Server errors
 */
apiClient.interceptors.response.use(
    (response) => {
        // For successful responses, return just the data
        return response.data;
    },
    (error: AxiosError) => {
        // Extract correlation ID from response headers (server returns it) or fallback to client-generated
        const responseHeaders = error.response?.headers;
        
        const responseCorrelationId = responseHeaders?.['x-correlation-id'] 
            || responseHeaders?.['X-Correlation-Id']
            || responseHeaders?.['x-correlationid']
            || responseHeaders?.['correlationid'];
        const correlationId = responseCorrelationId 
            || (error.config as any)?._correlationId as string | undefined;

        if (error.response) {
            const status = error.response.status;
            const data = error.response.data as any;
            const headers = error.response.headers;

            switch (status) {
                case 400:
                case 409: {
                    // Pass through structured server error body
                    const mapiError = MapiError.fromResponse(error.response);
                    mapiError.correlationId = correlationId;
                    return Promise.reject(mapiError);
                }

                case 401: {
                    logger.trackWarning('401 Unauthorized – clearing auth state', 'apiClient.response');

                    // Check www-authenticate header for specific identity errors
                    const wwwAuth = (headers as any)?.['www-authenticate'] || '';
                    let message = 'Unauthorized request.';
                    if (/identity_not_confirmed/i.test(wwwAuth)) {
                        message = 'User status is Pending. Please confirm your account.';
                    } else if (/invalid_identity/i.test(wwwAuth)) {
                        message = 'Invalid email or password.';
                    }

                    // Clear ALL tokens from Redux (mirrors old project pattern)
                    store.dispatch(clearAuth());

                    const unauthorizedErr = new UnauthorizedError(message);
                    (unauthorizedErr as any).correlationId = correlationId;
                    return Promise.reject(unauthorizedErr);
                }

                case 403: {
                    const forbiddenErr = new RequestError("You're not authorized to perform this operation.", 403);
                    (forbiddenErr as any).correlationId = correlationId;
                    return Promise.reject(forbiddenErr);
                }

                case 404: {
                    const notFoundError = new RequestError('Resource not found', 404);
                    (notFoundError as any).code = 'ResourceNotFound';
                    (notFoundError as any).correlationId = correlationId;
                    return Promise.reject(notFoundError);
                }

                case 408: {
                    const timeoutErr = new RequestError('Could not complete the request. Please try again later.', 408);
                    (timeoutErr as any).correlationId = correlationId;
                    return Promise.reject(timeoutErr);
                }

                case 429: {
                    const rateLimitErr = new RequestError('Too many attempts. Please try later.', 429);
                    (rateLimitErr as any).correlationId = correlationId;
                    return Promise.reject(rateLimitErr);
                }

                case 500:
                case 502:
                case 503: {
                    const serverErr = new RequestError('Internal server error. Please try again later.', status);
                    (serverErr as any).correlationId = correlationId;
                    return Promise.reject(serverErr);
                }

                default:
                    logger.trackError(
                        new RequestError(data?.message || error.message || `HTTP ${status}`, status),
                        'apiClient.response'
                    );
                    const defaultErr = new RequestError(
                        data?.message || error.message || `Unexpected error (HTTP ${status})`,
                        status
                    );
                    (defaultErr as any).correlationId = correlationId;
                    return Promise.reject(defaultErr);
            }
        } else if (error.request) {
            // Request was made but no response received (network error)
            logger.trackError(new Error('Network error – unable to reach the server'), 'apiClient.response');
            const networkErr = new RequestError('Network error: Unable to reach the server. Please check your connection.');
            (networkErr as any).correlationId = correlationId;
            return Promise.reject(networkErr);
        } else {
            // Something else happened during request setup
            logger.trackError(error instanceof Error ? error : new Error(String(error)), 'apiClient.response');
            const unknownErr = new RequestError(error.message || 'An unexpected error occurred.');
            (unknownErr as any).correlationId = correlationId;
            return Promise.reject(unknownErr);
        }
    }
);

/**
 * Type-Safe HTTP Methods with Generics
 * 
 * These wrapper methods provide TypeScript generic support
 * so services can specify exact return types
 */

/**
 * GET request
 * @param url - API endpoint URL
 * @param config - Optional Axios request config
 * @returns Promise<T> - Typed response data
 * @example
 * const users = await apiClient.get<User[]>('/users');
 */
export const get = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.get<T>(url, config) as any;
};

/**
 * POST request
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param config - Optional Axios request config
 * @returns Promise<T> - Typed response data
 * @example
 * const newUser = await apiClient.post<User>('/users', { name: 'John' });
 */
export const post = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.post<T>(url, data, config) as any;
};

/**
 * PUT request
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param config - Optional Axios request config
 * @returns Promise<T> - Typed response data
 * @example
 * const updated = await apiClient.put<User>('/users/1', { name: 'Jane' });
 */
export const put = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.put<T>(url, data, config) as any;
};

/**
 * PATCH request
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param config - Optional Axios request config
 * @returns Promise<T> - Typed response data
 */
export const patch = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.patch<T>(url, data, config) as any;
};

/**
 * DELETE request
 * @param url - API endpoint URL
 * @param config - Optional Axios request config
 * @returns Promise<T> - Typed response data
 * @example
 * await apiClient.delete('/users/1');
 */
export const del = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.delete<T>(url, config) as any;
};

/**
 * Export the configured instance and typed methods
 */
export default {
    get,
    post,
    put,
    patch,
    delete: del,
    // Export the raw instance for advanced use cases
    instance: apiClient,
};
